import type { Holiday } from '@erp/shared';
import { CalendarHeart, Pencil, Plus, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { Button, Card, confirmDialog, DataTable, type DataTableColumn, EmptyState, FormField, Input, Skeleton } from '../../../components/ui/index.js';
import {
  attendanceErrorCode,
  createHoliday,
  deleteHoliday,
  listHolidays,
  localToday,
  updateHoliday,
} from '../../../lib/attendance.js';

/** Days in an inclusive `YYYY-MM-DD` range. */
function dayCount(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000) + 1;
}

interface Draft { id: string | null; name: string; startDate: string; endDate: string }

/**
 * Public holidays. Iraq's move with the moon (both Eids, Mawlid) and with the
 * government's announcements, so they are entered per year rather than
 * shipped as a calendar: a holiday is a range, and no day inside it is an absence.
 */
export function HolidaysPanel(): JSX.Element {
  const { t } = useTranslation('hr');
  const [year, setYear] = useState(() => Number(localToday().slice(0, 4)));
  const [items, setItems] = useState<Holiday[] | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async (): Promise<void> => {
    try { setItems(await listHolidays(year)); } catch { setItems([]); }
  }, [year]);
  useEffect(() => { void reload(); }, [reload]);

  const fail = (err: unknown): void => {
    const code = attendanceErrorCode(err);
    toast.error(t(`attendance.errors.${code ?? 'generic'}`, { defaultValue: t('attendance.errors.generic') }));
  };

  const save = async (): Promise<void> => {
    if (!draft || !draft.name.trim() || !draft.startDate) return;
    const endDate = draft.endDate || draft.startDate;
    setSaving(true);
    try {
      const body = { name: draft.name.trim(), startDate: draft.startDate, endDate };
      if (draft.id) await updateHoliday(draft.id, body);
      else await createHoliday(body);
      toast.success(t('attendance.sessions.saved'));
      setDraft(null);
      await reload();
    } catch (err) {
      fail(err);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (h: Holiday): Promise<void> => {
    if (!(await confirmDialog({ message: t('attendance.holidays.confirmDelete', { name: h.name }), tone: 'danger' }))) return;
    try {
      await deleteHoliday(h.id);
      await reload();
    } catch (err) {
      fail(err);
    }
  };

  const columns: DataTableColumn<Holiday>[] = [
    {
      id: 'name',
      header: t('attendance.holidays.name'),
      sortValue: (h) => h.name,
      searchValue: (h) => h.name,
      cellClassName: 'font-medium',
      cell: (h) => h.name,
    },
    {
      id: 'from',
      header: t('attendance.holidays.from'),
      sortValue: (h) => h.startDate,
      cellClassName: 'font-mono text-xs',
      cell: (h) => h.startDate,
    },
    {
      id: 'to',
      header: t('attendance.holidays.to'),
      sortValue: (h) => h.endDate,
      cellClassName: 'font-mono text-xs',
      cell: (h) => h.endDate,
    },
    {
      id: 'days',
      header: t('attendance.holidays.days'),
      align: 'end',
      sortValue: (h) => dayCount(h.startDate, h.endDate),
      cellClassName: 'font-mono tabular-nums',
      cell: (h) => dayCount(h.startDate, h.endDate),
    },
    {
      id: 'actions',
      header: '',
      align: 'end',
      headClassName: 'w-24',
      cell: (h) => (
        <div className="flex justify-end gap-1">
          <Button variant="ghost" size="sm" onClick={() => setDraft({ id: h.id, name: h.name, startDate: h.startDate, endDate: h.endDate })} title={t('attendance.sessions.edit')} aria-label={t('attendance.sessions.edit')}>
            <Pencil className="h-4 w-4" aria-hidden />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => void remove(h)} title={t('attendance.sessions.delete')} aria-label={t('attendance.sessions.delete')}>
            <Trash2 className="h-4 w-4" aria-hidden />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-fg-muted">{t('attendance.holidays.year')}</span>
          <select
            value={year}
            onChange={(e) => setYear(Number(e.target.value))}
            className="h-9 rounded-md border border-border bg-bg px-2 text-sm text-fg outline-none focus:border-brand"
          >
            {Array.from({ length: 5 }, (_, i) => Number(localToday().slice(0, 4)) - 2 + i).map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </label>
        {!draft && (
          <Button onClick={() => setDraft({ id: null, name: '', startDate: `${year}-01-01`, endDate: '' })} className="gap-1.5">
            <Plus className="h-4 w-4" aria-hidden />
            {t('attendance.holidays.add')}
          </Button>
        )}
      </div>

      {draft && (
        <Card className="p-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
            <div className="md:col-span-2">
              <FormField id="hol-name" label={t('attendance.holidays.name')} required>
                <Input id="hol-name" value={draft.name} maxLength={120} onChange={(e) => setDraft({ ...draft, name: e.target.value })} autoFocus />
              </FormField>
            </div>
            <FormField id="hol-from" label={t('attendance.holidays.from')} required>
              <Input id="hol-from" type="date" value={draft.startDate} onChange={(e) => setDraft({ ...draft, startDate: e.target.value })} />
            </FormField>
            <FormField id="hol-to" label={t('attendance.holidays.to')} helperText={t('attendance.holidays.toHint')}>
              <Input id="hol-to" type="date" value={draft.endDate} min={draft.startDate} onChange={(e) => setDraft({ ...draft, endDate: e.target.value })} />
            </FormField>
          </div>
          <div className="mt-3 flex gap-2">
            <Button size="sm" loading={saving} onClick={() => void save()} disabled={!draft.name.trim() || !draft.startDate}>
              {t('attendance.sessions.save')}
            </Button>
            <Button size="sm" variant="secondary" disabled={saving} onClick={() => setDraft(null)}>
              {t('attendance.sessions.cancel')}
            </Button>
          </div>
        </Card>
      )}

      {!items ? (
        <Card className="p-4"><Skeleton className="h-24 w-full" /></Card>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<CalendarHeart className="h-8 w-8" aria-hidden />}
          title={t('attendance.holidays.empty', { year })}
          description={t('attendance.holidays.emptyDescription')}
        />
      ) : (
        <Card className="overflow-hidden p-0">
          <DataTable columns={columns} data={items} rowKey={(h) => h.id} paginated={false} searchable={false} />
        </Card>
      )}
    </div>
  );
}
